// The closing detail dialog — ported from mantis-business-front-brinks's cierre-dialog.tsx onto
// the four real endpoints this session's already established: `getCierreClosingDetail` (BR-14's
// `get_closing`, presented), `getCierreDocuments` (BR-30's signed URLs), the alerts embedded in
// the detail response, and `overrideCierreDatapoint` (BR-31) for real editing.
//
// Brinks' own dialog lets you "Editar" any of its mock fields with no save — `setValores` is
// local state that vanishes on close. That can't be ported honestly: this backend's real
// correction route (`docs/CIERRES_CORRECTIONS.md`) only accepts a PANEL field (a raw extracted
// value), never a CALCULATION — "Diferencia cuadre" etc. are recomputed from datapoints on every
// evaluation, and a value written against one would be discarded by the next sweep and the route
// refuses it outright. So this dialog has two visually similar but functionally different
// sections under one "Editar" toggle (matching brinks' single header button, not a pencil per
// field): "Campos capturados" (the panel's own fields, real, individually gated by the backend's
// own `correctable` flag — only those grow an input when editing) and "Resultados calculados"
// (`detail.calculations`, always read-only). A real correction also needs a reason — brinks has
// none — so entering edit mode reveals one shared reason field; every changed value in that save
// is sent with it. `panelRow` — the flat row a caller already fetched from `filterOffices` — is
// what supplies the *current* value of each panel field; without it the section explains why
// instead of guessing.
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle,
  ChevronDown,
  Download,
  FileText,
  Info,
  Paperclip,
  PenLine,
  RotateCcw,
  Settings2,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Textarea,
  cn,
} from "@mantis-sas/ui";

import {
  downloadCierreClosingArchive,
  filterOffices,
  getCierreClosingDetail,
  getCierreDocuments,
  getExtractionFields,
  overrideCierreDatapoint,
  sendCierreMissingDocumentsNotice,
} from "../../../ApiCalls/mantisBusiness";
import "./styles/brinksTheme.css";
import { SEVERITY_TYPES, toResultView, toStepView } from "../../../utils/cierreClosingDetail";
import { montoEscrito } from "../../../utils/cierreCuadreDia";
import { formatMoney } from "../../../utils/formatMoney";
import {
  ajusteDeAlerta,
  archivoDeDocumento,
  atencionDeAlerta,
  observacionDeAlerta,
  alertasVigentes,
  esAlertaAbierta,
  esAlertaAtendidaVisible,
  estadoDeDetalle,
  datoLegible,
  montoLegible,
  ordenarAlertasPorSeveridad,
  pendientesDelCierre,
  documentosPendientes,
  resultadoDeAjuste,
  tonoDeAjuste,
  resultadoDeCorreccion,
} from "../../../utils/cierrePanel";
import { correctionError, isCierreRegister } from "../../../utils/cierreCorrections";
import { accionesDeCierre, objetivoDeCierre, puedeTrabajarAMano } from "../../../utils/cierrePermisos";
import { documentosDeAlerta, fechaDMY, fechaHoraBogota } from "../../../utils/cierrePanel";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import { AVISO_ALERTAS } from "../../../utils/cierreManual";
import { llegadaEnPalabras, spreadsheetViewerUrl, unavailableMessage, viewerKind } from "../../../utils/cierreDocuments";
import { useAtenderAlerta, useEditarObservacion } from "./AtenderAlerta";
import { CorreoAOficina } from "./CorreoAOficina";
import { VistaHojaDeCalculo } from "./VistaHojaDeCalculo";
import { VistaPreviaHoja } from "./VistaPreviaHoja";
import { VisorImagen } from "./VisorImagen";
import { VariableDesplegable } from "./VariableDesplegable";
import {
  NOMBRES_VACIOS,
  ayudaDeAlerta,
  detallesDeCalculo,
  detallesDeDato,
  mensajeDeAlerta,
  nombreDeAlerta,
  nombreDeCalculo,
  nombreDeDato,
  nombreDeFuente,
} from "../../../utils/cierreNombres";
import { EstadoBadge } from "./EstadoBadge";
import { RehacerCierreDialog } from "./RehacerCierreDialog";
import { SubirDocumentosOficina } from "./SubirDocumentosOficina";
import { PendientesOficina } from "./PendientesOficina";
import { ReversasOficina } from "./ReversasOficina";
import { useClienteCierres } from "./ClienteCierresContext";
import { GloryCelda, useGloryOficinas } from "./GloryOficina";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const TONO_ALERTA = {
  ALERT: "border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger",
  SUBALERT: "border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning",
  INFO: "border-brinks-success-border bg-brinks-success-soft text-brinks-success",
  // A difference inherited from an earlier day: information here, attended on the day it started.
  CARRIED: "border-brinks-border-strong bg-brinks-surface text-brinks-muted-foreground",
};

const SCROLL_OCULTO = "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

function valorComoTexto(valor) {
  return valor === null || valor === undefined ? "" : String(valor);
}

// One alert of the closing — B-56.
//
// Its own component because attending needs state per alert and a hook cannot live inside a `.map`
// callback. With `onAtendida` it is an open alert and offers the observation box; without it, it is
// one already attended and shows who attended it and what they wrote. BR-38: an open alert offers
// "Atender" only to someone who may attend this closing's alerts — to anyone else it is a reading.
// With `onEditada`, an alert a PERSON attended also offers to change its observation (same permission).
// B-103 — an open `documentos_faltantes` also offers "Correo a la oficina": what it still has to send,
// previewed and approved before it goes to the office's own addresses.
// A `carried` alert is a difference this closing inherited from an earlier day: never attended here —
// it offers "Ir al cierre del …" for each day it started on (`onIrACierre`), and is attended there.
// B-144 — the documents an alert cites that this closing has, each one button that opens it in the viewer.
// B-149 — an office email's other file: drawn when it is a PDF, an image or a workbook, offered for
// download otherwise (a zip, a Word file).
const ADJUNTO_DEL_CORREO = "Adjunto del correo";
const adjuntoVisible = (adjunto) => {
  const tipo = String(adjunto?.mimeType || "").toLowerCase();
  return tipo === "application/pdf" || tipo.startsWith("image/") || viewerKind(adjunto) !== "frame";
};

function documentosCitados(alerta, documentos) {
  return documentosDeAlerta(alerta)
    .map((citado) => (documentos || []).find((doc) => doc.sourceKey === citado.sourceKey))
    .filter(Boolean)
    .filter((doc, i, todos) => todos.findIndex((otro) => otro.id === doc.id) === i);
}

function AlertaDelCierre({
  alerta, nombres, closingId, token, mantisBusinessId, onAtendida, onEditada, onIrACierre, puedeAtender = false,
  documentos = [], docSeleccionado = null, onVerDocumento,
}) {
  const bucket = SEVERITY_TYPES[alerta.severity] || "INFO";
  const arrastrada = bucket === "CARRIED";
  const origenes = arrastrada ? alerta.carriedFrom?.origins || [] : [];
  const atendible = bucket !== "INFO" && !arrastrada;
  const [correo, setCorreo] = useState(false);
  const [enviado, setEnviado] = useState(null);
  const pideCorreo = alerta.ruleKey === "documentos_faltantes" && alerta.status === "OPEN" && bucket !== "INFO";
  const atencion = atencionDeAlerta(alerta);
  const atender = useAtenderAlerta({
    alertaId: alerta.id,
    closingId,
    token,
    mantisBusinessId,
    onAtendida,
    compacto: true,
    ajustable: alerta.adjustable,
  });
  const editar = useEditarObservacion({
    alerta,
    closingId,
    token,
    mantisBusinessId,
    onEditada,
    compacto: true,
  });
  const puedeEditar = !!onEditada && puedeAtender && alerta.status === "RESOLVED_BY_USER" && !editar.abierto;
  const citados = onVerDocumento && bucket !== "INFO" ? documentosCitados(alerta, documentos) : [];

  return (
    <div className={cn("rounded-[9px] border-l-2 px-2.5 py-2.5", TONO_ALERTA[bucket], !onAtendida && "opacity-70")}>
      <div className="flex gap-2.5">
        <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-card">
          {arrastrada ? (
            <Info className="h-3.5 w-3.5" />
          ) : bucket === "INFO" ? (
            <CheckCircle className="h-3.5 w-3.5" />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          {citados.length ? (
            <button
              type="button"
              className="text-left text-[12px] font-semibold text-brinks-foreground hover:underline"
              title={ayudaDeAlerta(nombres, alerta.ruleKey) || "Ver el documento de esta alerta"}
              onClick={() => onVerDocumento(citados[0])}
            >
              {nombreDeAlerta(nombres, alerta.ruleKey)}
            </button>
          ) : (
            <p className="text-[12px] font-semibold text-brinks-foreground" title={ayudaDeAlerta(nombres, alerta.ruleKey) || undefined}>
              {nombreDeAlerta(nombres, alerta.ruleKey)}
            </p>
          )}
          <p className="mt-0.5 text-[11px] leading-snug text-brinks-muted-foreground">{mensajeDeAlerta(nombres, alerta.message)}</p>
          {citados.length ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              {citados.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => onVerDocumento(doc)}
                  aria-pressed={docSeleccionado?.id === doc.id}
                  className={cn(
                    "inline-flex h-6 max-w-full items-center gap-1 rounded-full border px-2 text-[10.5px] font-medium",
                    docSeleccionado?.id === doc.id
                      ? "border-brinks-foreground bg-brinks-foreground text-card"
                      : "border-brinks-border-strong bg-card text-brinks-foreground hover:bg-brinks-surface-hover",
                  )}
                  title={archivoDeDocumento(doc) || doc.sourceKey}
                >
                  <FileText aria-hidden className="h-3 w-3 shrink-0" />
                  <span className="truncate">Ver {nombreDeFuente(nombres, doc.sourceKey)}</span>
                </button>
              ))}
            </div>
          ) : null}
          {atencion ? (
            <p className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-brinks-muted-foreground">
              <Check aria-hidden className="mt-0.5 h-3 w-3 shrink-0" />
              <span className="font-medium text-brinks-foreground">{atencion}</span>
            </p>
          ) : null}
          {atencion && ajusteDeAlerta(alerta) ? (
            <p className={cn("mt-0.5 pl-[18px] text-[11px] font-medium leading-snug", tonoDeAjuste(alerta.adjustment?.amount))}>
              {ajusteDeAlerta(alerta)}
            </p>
          ) : null}
          {/* The actions in a row under the message, side by side while they fit — never a column beside
              it, which squeezes the message into a word per line. An `info` alert ("Caja cuadrada
              $0,00") is good news: nothing to attend. */}
          {arrastrada && onIrACierre && origenes.length ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {origenes.map((origen) => (
                <Button
                  key={origen.closingId}
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1"
                  onClick={() => onIrACierre(origen.closingId, origen.businessDate)}
                >
                  Ir al cierre del {fechaDMY(origen.businessDate)}
                  {origenes.length > 1 ? ` ($${formatMoney(origen.amount)})` : ""}
                  <ArrowRight aria-hidden className="h-3.5 w-3.5" />
                </Button>
              ))}
            </div>
          ) : null}
          {(onAtendida && puedeAtender && ((pideCorreo && !correo) || (atendible && !atender.abierto))) || puedeEditar ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {onAtendida && puedeAtender && pideCorreo && !correo ? (
                <Button variant="outline" size="sm" className="h-7" onClick={() => setCorreo(true)}>Correo a la oficina</Button>
              ) : null}
              {onAtendida && puedeAtender && atendible && !atender.abierto ? atender.boton : null}
              {puedeEditar ? editar.boton : null}
            </div>
          ) : null}
        </div>
      </div>
      {enviado ? (
        <p role="status" className="mt-1.5 text-[11px] text-brinks-success">
          Correo a la oficina enviado a {enviado.sentTo.join(", ")} por {enviado.sentBy}.
        </p>
      ) : null}
      {correo ? (
        <CorreoAOficina
          titulo="Correo a la oficina: documentos que faltan"
          enviar={(preview, { to, comment }) => sendCierreMissingDocumentsNotice(token, mantisBusinessId, {
            closingId, to, comment, preview,
          })}
          onCancelar={() => setCorreo(false)}
          onEnviado={(data) => {
            setCorreo(false);
            setEnviado(data);
          }}
        />
      ) : null}
      {/* B-83 — the observation is what the next person reads, so it gets the card's whole width,
          the body's size and colour, and a label — not a grey, italic line squeezed beside a
          button. Not truncated: it is a sentence a person wrote for the next person to read. */}
      {/* B-116 — for one resolved by a person's correction, the observation is the correction's reason. */}
      {atencion && observacionDeAlerta(alerta) && !editar.abierto ? (
        <div className="mt-2 rounded-[7px] border border-brinks-border bg-card px-2.5 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-brinks-muted-foreground">Observación</p>
          <p className="mt-0.5 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-brinks-foreground">
            {observacionDeAlerta(alerta)}
          </p>
        </div>
      ) : null}
      {atender.formulario ? <div className="mt-2">{atender.formulario}</div> : null}
      {editar.formulario ? <div className="mt-2">{editar.formulario}</div> : null}
    </div>
  );
}

// B-80 — a closing an agent is working, or a cancelled one, is not redone.
const SIN_REHACER = ["RUNNING", "CANCELLED"];

function Contenido({ closingId, panelRow, token, mantisBusinessId, onTrabajarAMano, onCambio, onIrACierre }) {
  const [cargando, setCargando] = useState(true);
  const [rehacerAbierto, setRehacerAbierto] = useState(false);
  const [detalle, setDetalle] = useState(null);
  // Why the detail did not load. Without it a failed request left the spinner up forever — the
  // dialog waited for a `detalle` that was never coming (e.g. the database unreachable).
  const [errorCarga, setErrorCarga] = useState(null);
  const [intento, setIntento] = useState(0);
  const [documentos, setDocumentos] = useState([]);
  // B-100 — how each document reached the closing (email, "Procesar el día", the panel).
  const [llegadas, setLlegadas] = useState([]);
  // B-149 — the office email's other files (its reports, annexes), without signature images.
  const [adjuntos, setAdjuntos] = useState([]);
  const [docSeleccionado, setDocSeleccionado] = useState(null);
  // BR-38 — why there are no documents, when the server said why: a person who may see the closing
  // but not its documents gets a 403 `DOCUMENTS_FORBIDDEN`, which is not "nothing was uploaded".
  const [errorDocumentos, setErrorDocumentos] = useState(null);
  // BR-38 — what this person may do to the closing, asked of its office once the detail names it.
  const acceso = useAccesoCierres();
  // B-55 — the column set and the names are the selected client's; the shell is this dialog's
  // ancestor, so it reads the same choice the screen behind it does. Not named `clientCode`:
  // that one is the closing's own, read off `detalle.closing` further down, and the two are
  // only the same string while the selector has not moved since this dialog opened.
  const { clientCode: clienteSeleccionado, diaEnCurso } = useClienteCierres();
  // B-101 — a client whose offices share the day's reports is redone as a day ("Rehacer el día", in the
  // day's detail where every office is), never one closing on its own.
  const rehacerEnElDia = diaEnCurso?.diario === true;
  const [camposExtraccion, setCamposExtraccion] = useState(null);
  // B-47 — the client's readable names (`panel/names/<client>.yaml`), served with the panel fields.
  const [nombres, setNombres] = useState(NOMBRES_VACIOS);
  const [valoresLocales, setValoresLocales] = useState({});
  const [alertasVisibles, setAlertasVisibles] = useState(true);
  // B-56 — shown once per open dialog, after the first alert is attended here.
  const [avisoReevaluacion, setAvisoReevaluacion] = useState(false);
  // B-115 — what an adjustment did to the following days, said once it is made.
  const [resultadoAjuste, setResultadoAjuste] = useState("");
  // B-88 — the whole closing as a ZIP (documents, extracted values, history), built by the server.
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState(null);

  // Editing "Datos extraídos" — one shared toggle for every correctable field, matching brinks'
  // single "Editar" button rather than a pencil per cell, plus the reason this real backend
  // requires that brinks' mock never needed.
  const [editandoDatos, setEditandoDatos] = useState(false);
  const [valoresEdicion, setValoresEdicion] = useState({});
  const [razonEdicion, setRazonEdicion] = useState("");
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [errorEdicion, setErrorEdicion] = useState(null);
  // B-116 — the confirmation before a correction is saved, and what it says when the closing could
  // not be recalculated at once.
  const [confirmandoCorreccion, setConfirmandoCorreccion] = useState(false);
  const [resultadoCorreccion, setResultadoCorreccion] = useState(null);
  const [cambiosConfirmados, setCambiosConfirmados] = useState([]);
  const [erroresCampo, setErroresCampo] = useState({});

  // B-134 — the office's "Máquina GLORY" switch, in "Configuración de la oficina" above the extracted
  // data: read-only there, changeable in "Editar" for whoever may. Its own dialog and its own save —
  // it is the office's setting, not this closing's figures, so it never joins the correction's draft.
  const oficinaDelCierre = detalle?.closing?.officeCode || panelRow?.codigo_oficina;
  const [avisoGlory, setAvisoGlory] = useState("");
  const glory = useGloryOficinas({
    token,
    mantisBusinessId,
    clientCode: detalle?.closing?.clientCode || clienteSeleccionado,
    officeCode: oficinaDelCierre,
    listo: isCierreRegister(panelRow) && Boolean(oficinaDelCierre),
    onGuardado: (texto) => {
      setAvisoGlory(texto);
      if (onCambio) onCambio();
    },
  });
  const estadoGlory = glory.soportado && oficinaDelCierre ? glory.gloryDe.get(String(oficinaDelCierre)) : null;

  useEffect(() => {
    let activo = true;
    (async () => {
      setCargando(true);
      setErrorCarga(null);
      // Each call fails on its own: a missing document list or name dictionary must not keep the
      // closing itself from showing, and a rejected promise must not leave the spinner up.
      const [detalleRes, docsRes, camposRes] = await Promise.all([
        getCierreClosingDetail(token, mantisBusinessId, closingId).catch((e) => ({ success: false, message: e?.message })),
        getCierreDocuments(token, mantisBusinessId, closingId).catch(() => null),
        getExtractionFields(token, mantisBusinessId, clienteSeleccionado).catch(() => null),
      ]);
      if (!activo) return;
      if (detalleRes?.success) setDetalle(detalleRes.data);
      // A closing the person lost access to while the panel was open is the 404 a missing one is,
      // and `correctionError` says so in words rather than as the backend's log line.
      else setErrorCarga(detalleRes?.errorCode || detalleRes?.message ? correctionError(detalleRes) : "No se pudo cargar el cierre.");
      const docs = docsRes?.success ? docsRes.data.documents : [];
      setDocumentos(docs);
      setLlegadas(docsRes?.success ? docsRes.data.arrivals || [] : []);
      setAdjuntos(docsRes?.success ? docsRes.data.attachments || [] : []);
      setErrorDocumentos(docsRes?.errorCode === "DOCUMENTS_FORBIDDEN" ? correctionError(docsRes) : null);
      const primero = docs.find((d) => d.url) || docs[0] || null;
      setDocSeleccionado(primero);
      if (camposRes?.success) {
        setCamposExtraccion(camposRes.fields);
        setNombres(camposRes.names || NOMBRES_VACIOS);
      }
      setCargando(false);
    })();
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clienteSeleccionado, closingId, intento]);

  const grupos = useMemo(
    () => (camposExtraccion ? Object.entries(camposExtraccion).filter(([, campos]) => campos.length > 0) : []),
    [camposExtraccion]
  );
  const camposCorrectables = useMemo(
    () => grupos.flatMap(([, campos]) => campos).filter((f) => f.correctable),
    [grupos]
  );

  const valorActual = (fieldName) =>
    fieldName in valoresLocales ? valoresLocales[fieldName] : panelRow?.[fieldName];

  const entrarEdicion = () => {
    setValoresEdicion(Object.fromEntries(camposCorrectables.map((f) => [f.fieldName, valorComoTexto(valorActual(f.fieldName))])));
    setRazonEdicion("");
    setErrorEdicion(null);
    setErroresCampo({});
    setEditandoDatos(true);
  };

  const restablecerEdicion = () => {
    setValoresEdicion(Object.fromEntries(camposCorrectables.map((f) => [f.fieldName, valorComoTexto(valorActual(f.fieldName))])));
    setErrorEdicion(null);
    setErroresCampo({});
  };

  const cancelarEdicion = () => {
    setEditandoDatos(false);
    setErrorEdicion(null);
    setErroresCampo({});
  };

  // B-56 — reload the whole detail rather than patching the one alert the way the feed does. Here
  // the resolution also moves the figures the dialog shows above it: the server marks the closing
  // for re-evaluation (`reevaluationQueued`), and a card quietly disagreeing with the state badge
  // beside it is the kind of two-tables-disagreeing defect this screen exists to avoid.
  //
  // B-115 — with an adjustment the following days were evaluated again, so the screen behind the
  // dialog (its list, its alerts) is told to reload too.
  const alertaAtendida = (resuelta, data) => {
    setIntento((n) => n + 1);
    // With an adjustment the "it will be raised again" warning is not true of the next day.
    if (data?.adjustment) {
      setResultadoAjuste(resultadoDeAjuste(data));
      if (onCambio) onCambio();
      return;
    }
    setAvisoReevaluacion(true);
  };

  // A changed observation reloads the detail too, so the card shows the stored note — but with no
  // re-evaluation notice: a note moves no figure and the server queues nothing.
  const observacionEditada = () => {
    setIntento((n) => n + 1);
  };

  const camposCambiados = () => camposCorrectables.filter(
    (f) => (valoresEdicion[f.fieldName] ?? "") !== valorComoTexto(valorActual(f.fieldName))
  );

  // B-116 — "Guardar" asks first: a dialog says what changes and that the closing is recalculated,
  // and the observation is required there. Nothing changed: leave edit mode.
  const pedirConfirmacion = () => {
    if (camposCambiados().length === 0) {
      setEditandoDatos(false);
      return;
    }
    setRazonEdicion("");
    setErrorEdicion(null);
    setResultadoCorreccion(null);
    setCambiosConfirmados(camposCambiados().map((field) => ({
      field, antes: valorActual(field.fieldName), despues: valoresEdicion[field.fieldName],
    })));
    setConfirmandoCorreccion(true);
  };

  const guardarEdicion = async () => {
    const cambiados = camposCambiados();
    if (!razonEdicion.trim()) {
      setErrorEdicion("Escribe la observación: por qué se corrige este cierre.");
      return;
    }
    setGuardandoEdicion(true);
    setErrorEdicion(null);
    const nuevosValores = {};
    const nuevosErrores = {};
    const respuestas = [];
    await Promise.all(
      cambiados.map(async (f) => {
        const response = await overrideCierreDatapoint(
          token,
          mantisBusinessId,
          closingId,
          f.fieldName,
          valoresEdicion[f.fieldName],
          razonEdicion.trim()
        );
        if (response?.success) {
          nuevosValores[f.fieldName] = response.data.value;
          respuestas.push(response.data);
        } else {
          nuevosErrores[f.fieldName] =
            response?.errorCode || response?.message ? correctionError(response) : "No se pudo guardar la corrección.";
        }
      })
    );
    setGuardandoEdicion(false);
    if (Object.keys(nuevosValores).length > 0) {
      setValoresLocales((prev) => ({ ...prev, ...nuevosValores }));
      setValoresEdicion((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.entries(nuevosValores).map(([k, v]) => [k, valorComoTexto(v)])),
      }));
    }
    setErroresCampo(nuevosErrores);
    if (Object.keys(nuevosErrores).length === 0) {
      setEditandoDatos(false);
    }
    // B-116 — the backend evaluated the closing with the correction: reload it, so its state, alerts
    // and figures show the result, and tell the screen behind the dialog. The confirmation closes
    // only when the correction cleared every alert — the change is then on screen. Otherwise it stays
    // to say what happened: what is still open, or that the closing could not be recalculated now.
    // A field the server refused closes it, and the form shows the refusal beside the field.
    if (respuestas.length > 0) {
      setIntento((n) => n + 1);
      if (onCambio) onCambio();
    }
    const evaluadas = respuestas.filter((r) => r.evaluated);
    const sinPendientes = evaluadas.length > 0 && !(evaluadas[evaluadas.length - 1].openAlerts || []).length;
    if (Object.keys(nuevosErrores).length > 0 || sinPendientes) {
      setConfirmandoCorreccion(false);
    } else {
      setResultadoCorreccion(resultadoDeCorreccion(respuestas, (clave) => nombreDeAlerta(nombres, clave)));
    }
  };

  if (!cargando && !detalle) {
    return (
      <div className="grid min-h-[300px] place-items-center p-10 text-center">
        <div className="space-y-3">
          <p className="text-[13px] font-medium text-brinks-foreground">No se pudo abrir el cierre.</p>
          <p className="max-w-[520px] text-[12px] text-brinks-muted-foreground">
            {errorCarga || "El servidor no respondió."}
          </p>
          <button
            type="button"
            onClick={() => setIntento((n) => n + 1)}
            className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-brinks-border px-3 text-[12.5px] font-medium text-brinks-foreground hover:bg-brinks-surface-hover"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (cargando) {
    return (
      <div className="grid min-h-[300px] place-items-center p-10">
        <Spinner size="lg" />
      </div>
    );
  }

  const estado = estadoDeDetalle(detalle);
  const resultados = (detalle.calculations || []).map(toResultView);
  // `detail.sources` carries every attempt, oldest first (the process view's own trail) — the
  // "Fuentes" list only wants the current one per source key, same as the document strip above.
  const pasos = [...new Map((detalle.sources || []).map(toStepView).map((p) => [p.label, p])).values()];
  // B-46 — red first, green last: the alerts that need a person sit at the top of the column.
  // B-83 — an open alert repeating one already attended (same rule, same amount) is not shown
  // twice: the attended card below stands for it (`alertasVigentes`).
  const alertasAbiertas = ordenarAlertasPorSeveridad(alertasVigentes(detalle.alerts || []).filter(esAlertaAbierta));
  // B-56 — and the ones already attended, underneath, with the observation whoever attended them
  // wrote. `closing_alerts` is append-only precisely so "flagged at 09:12, closed by a person at
  // 09:40 with this note" survives, and this is the surface where that note is read: the mural asks
  // for the documents and the differences in one place, which is this dialog.
  const alertasAtendidas = (detalle.alerts || []).filter(esAlertaAtendidaVisible);
  // B-46 — "Pendientes para cuadrar": what the unresolved calculations actually lacked, grouped
  // by the document or source it comes from, and whether that one is loaded. It replaced a list of
  // the documents an open alert cites, which named an uploaded cuadre as "Pendiente" whenever the
  // bank's informe was the thing missing. See `pendientesDelCierre`.
  const pendientes = pendientesDelCierre(resultados, pasos);
  // B-132 — and the documents an open `documentos_faltantes` still asks for (the signed arqueo on a day
  // with a difference): pending too, said here and not only among the alerts.
  const documentosSinEnviar = documentosPendientes(detalle.alerts || []);

  // B-47 — what a variable's details panel needs: the live datapoint behind a key, the file it was
  // read from, and a calculation's inputs. Looked up here once, not per card.
  const datoVivo = new Map((detalle.datapoints || []).map((d) => [d.key, d.live]));
  const entradasDeCalculo = new Map((detalle.calculations || []).map((c) => [c.key, c.inputs || []]));
  const resultadoPorClave = new Map(resultados.map((r) => [r.key, r]));
  const archivoDeFuente = (fuente) => archivoDeDocumento(documentos.find((d) => d.sourceKey === fuente));
  const nombreDeCampo = (field) => {
    if (field.calcKey) return nombres.calculations?.[field.calcKey]?.label || field.friendlyName;
    if (field.datapointKey) return nombres.fields?.[field.datapointKey]?.label || field.friendlyName;
    return field.friendlyName;
  };
  const detallesDeCampo = (field) => {
    if (field.calcKey) {
      return detallesDeCalculo({
        nombres,
        resultado: resultadoPorClave.get(field.calcKey) || { key: field.calcKey },
        entradas: entradasDeCalculo.get(field.calcKey),
      });
    }
    if (!field.datapointKey) return [];
    return detallesDeDato({
      nombres,
      ref: field.datapointKey,
      vivo: datoVivo.get(field.datapointKey),
      archivo: field.sourceKey ? archivoDeFuente(field.sourceKey) : null,
    });
  };

  // The dialog is reached from a panel that already scrolls across every office of one client, so
  // naming the client here ("davivienda") tells the operator nothing they didn't already know —
  // the office is the thing that actually varies from row to row and was missing entirely.
  const { officeName, officeCode, clientCode } = detalle.closing || {};
  // The closing's own client and office; the region only the panel row carries.
  const objetivo = objetivoDeCierre({
    cliente: clientCode || clienteSeleccionado,
    oficina: officeCode || panelRow?.codigo_oficina,
    region: panelRow?.region,
  });
  const acciones = accionesDeCierre(acceso, objetivo);
  // "Editar" opens for whoever may correct a figure or switch the office's GLORY (B-134); only the
  // first one turns the fields into inputs and gets "Guardar".
  const puedeCorregir = camposCorrectables.length > 0 && acciones.corregir;
  const puedeRehacerAqui = isCierreRegister(panelRow) && acciones.rehacer && !rehacerEnElDia && !SIN_REHACER.includes(detalle.closing?.state);
  const oficinaLabel = officeName && officeCode
    ? `${officeName} (${officeCode})`
    : officeName || officeCode || clientCode || "";
  const officeLabel = `${oficinaLabel} · ${detalle.closing?.businessDate || ""}`;

  // B-88 — "Descargar todo": every current document, the extracted values and the closing's whole
  // history in one ZIP. The server builds it (the documents sit in a private bucket and the history
  // is not served to this screen); the name is built here, because the browser hides the server's.
  const descargarTodo = async () => {
    setDescargando(true);
    setErrorDescarga(null);
    const nombre = `cierre_${officeCode || "oficina"}_${detalle.closing?.businessDate || "sin-fecha"}.zip`;
    const respuesta = await downloadCierreClosingArchive(token, mantisBusinessId, closingId, nombre).catch(() => null);
    if (respuesta?.success && respuesta.blob) {
      const { saveAs } = await import("file-saver");
      saveAs(respuesta.blob, respuesta.filename);
    } else {
      setErrorDescarga(
        respuesta?.errorCode || respuesta?.message ? correctionError(respuesta) : "No se pudo descargar el cierre."
      );
    }
    setDescargando(false);
  };

  // A "Fuentes" chip's label is the source key `toStepView` fell back to — the same string
  // `getCierreDocuments` reports as each document's `sourceKey` — so clicking one just needs to
  // find the matching document and hand it to the same `setDocSeleccionado` the dropdown above
  // already uses. A pending source (`!p.ok`, nothing gathered yet) has no document to switch to.
  const seleccionarDocumentoDeFuente = (paso) => {
    if (!paso.ok) return;
    const doc = documentos.find((d) => d.sourceKey === paso.label);
    if (doc) setDocSeleccionado(doc);
  };

  return (
    <>
      <DialogHeader className="shrink-0 border-b bg-card p-3.5 text-left">
        {/* B-174 — on a phone or a tablet the actions go under the office, in a row that wraps; from
            `md` up, beside it. The close button keeps its corner either way. */}
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
          <div className="flex min-w-0 items-start gap-3 pr-8">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-brinks-surface text-brinks-foreground">
              <Building2 className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1">
                <DialogTitle className="min-w-0 truncate text-[16px] font-semibold">{officeLabel}</DialogTitle>
                <BotonGuia guiaId="dialogo-detalle-cierre" titulo="Cierre de la oficina" texto="Todo el cierre de una oficina en un día: su estado, las cifras leídas de sus documentos, los documentos, las alertas y lo que falta para cuadrar." nombre="Cierre de la oficina" />
              </div>
              <div {...guia("Estado del cierre", "Cuadrado, Pendiente revisión o Bloqueado. Un cierre se bloquea solo cuando hay un descuadre abierto.")} className="mt-1.5 flex flex-wrap items-center gap-2">
                <EstadoBadge estado={estado} />
                {detalle.closing?.redoneAt ? (
                  <span className="text-[11.5px] text-brinks-muted-foreground">
                    Rehecho el {fechaDMY(String(detalle.closing.redoneAt).slice(0, 10))} · lo atendido antes ya no cuenta
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:mr-8 md:shrink-0 md:flex-nowrap">
            {/* B-40 — the manual pipeline on this closing. Asked of the ROW: only a cierre row has a
                worksheet, and a legacy register reaching this dialog must not be offered one. */}
            {/* B-88 — the whole closing, not the one document on screen (that one keeps its own
                icon above the viewer). Hidden only when the person may not see the documents: the
                ZIP carries them, so the server would refuse it the same way. */}
            {!errorDocumentos ? (
              <button
                type="button"
                {...guia("Descargar todo", "Descarga el cierre completo en un ZIP: todos sus documentos, los valores extraídos y su histórico.")}
                onClick={descargarTodo}
                disabled={descargando}
                title="Documentos, valores extraídos e histórico del cierre en un ZIP"
                className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-[8px] border border-brinks-border px-2.5 text-[12.5px] font-medium text-brinks-foreground hover:bg-brinks-surface-hover disabled:opacity-60"
              >
                {descargando ? <Spinner size="sm" /> : <Download className="h-3.5 w-3.5" />}
                {descargando ? "Preparando ZIP…" : "Descargar todo"}
              </button>
            ) : null}
            {/* B-100 — the office's own files (by email, or here when one did not arrive or came
                wrong), and the closing analysed again. Only for a client whose offices send some. */}
            {isCierreRegister(panelRow) ? (
              <SubirDocumentosOficina
                token={token}
                mantisBusinessId={mantisBusinessId}
                closingId={closingId}
                oficina={detalle.closing?.officeName}
                nombres={nombres}
                permitido={acciones.cargar}
                cerrado={["BALANCED", "RESOLVED", "CANCELLED"].includes(detalle.closing?.state)}
                onSubido={() => {
                  setIntento((n) => n + 1);
                  if (onCambio) onCambio();
                }}
              />
            ) : null}
            {/* B-80 — start the closing again from its reports, after saying what is lost. */}
            {isCierreRegister(panelRow) && acciones.rehacer && !rehacerEnElDia && !SIN_REHACER.includes(detalle.closing?.state) ? (
              <button
                type="button"
                {...guia("Rehacer", "Empieza el cierre otra vez desde sus reportes. Antes te dice qué correcciones y alertas atendidas se pierden.")}
                onClick={() => setRehacerAbierto(true)}
                className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-[8px] border border-brinks-border px-2.5 text-[12.5px] font-medium text-brinks-foreground hover:bg-brinks-surface-hover"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Rehacer
              </button>
            ) : null}
            {onTrabajarAMano && isCierreRegister(panelRow) && puedeTrabajarAMano(acciones) ? (
              <button
                type="button"
                {...guia("Trabajar a mano", "Sube un documento, escribe o corrige cifras y evalúa el cierre tú mismo.")}
                onClick={() => onTrabajarAMano(closingId, objetivo.region)}
                className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-[8px] border border-brinks-border px-2.5 text-[12.5px] font-medium text-brinks-foreground hover:bg-brinks-surface-hover"
              >
                <PenLine className="h-3.5 w-3.5" /> Trabajar a mano
              </button>
            ) : null}
          </div>
        </div>
        {errorDescarga ? (
          <p role="alert" className="mt-2 text-[11.5px] text-brinks-danger">
            {errorDescarga}
          </p>
        ) : null}
      </DialogHeader>

      {glory.dialogos}

      <RehacerCierreDialog
        open={rehacerAbierto}
        onOpenChange={setRehacerAbierto}
        token={token}
        mantisBusinessId={mantisBusinessId}
        closingId={closingId}
        nombres={nombres}
        onRehecho={() => {
          setIntento((n) => n + 1);
          if (onCambio) onCambio();
        }}
      />

      {/* Only the body scrolls: the header and the dialog's close button stay put above it. */}
      <div className={cn("relative min-h-0 flex-1 space-y-2 overflow-y-auto bg-background p-2", SCROLL_OCULTO)}>
        <div
          className={cn(
            "grid gap-2 lg:grid-cols-2",
            alertasVisibles
              ? "xl:grid-cols-[minmax(320px,1fr)_minmax(380px,1.3fr)_minmax(260px,0.8fr)]"
              : "xl:grid-cols-[minmax(320px,0.8fr)_minmax(560px,1.8fr)]"
          )}
        >
          {/* Datos extraídos: campos reales del panel (editables) + resultados calculados (no editables) */}
          <section {...guia("Datos extraídos", "Las cifras leídas de los documentos y los resultados del cuadre. Toca una cifra para ver de dónde sale. Con \"Editar\" corriges una cifra; queda registrado con tu nombre y una observación.")} className="overflow-hidden rounded-[12px] border border-brinks-border-strong bg-card shadow-brinks-card">
            <div className="flex min-h-11 items-center justify-between gap-2 border-b bg-brinks-surface px-3.5 py-2">
              <div className="min-w-0">
                <h3 className="text-[12px] font-semibold text-brinks-foreground">Datos extraídos</h3>
                <p className="mt-0.5 truncate text-[10.5px] text-brinks-muted-foreground">
                  Campos capturados del documento y resultados del cuadre
                </p>
              </div>
              {puedeCorregir || estadoGlory?.editable ? (
                editandoDatos && !puedeCorregir ? (
                  <button
                    type="button"
                    onClick={cancelarEdicion}
                    className="inline-flex h-7 shrink-0 items-center gap-1 rounded-[6px] bg-primary px-2 text-[11px] font-medium text-primary-foreground"
                  >
                    <Check className="h-3.5 w-3.5" /> Listo
                  </button>
                ) : editandoDatos ? (
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={restablecerEdicion}
                      disabled={guardandoEdicion}
                      aria-label="Restablecer"
                      title="Restablecer"
                      className="grid h-7 w-7 place-items-center rounded-[6px] text-brinks-muted-foreground hover:bg-card"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={cancelarEdicion}
                      disabled={guardandoEdicion}
                      className="rounded-[6px] px-2 text-[11px] font-medium text-brinks-muted-foreground hover:bg-card"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={pedirConfirmacion}
                      disabled={guardandoEdicion}
                      className="inline-flex h-7 items-center gap-1 rounded-[6px] bg-primary px-2 text-[11px] font-medium text-primary-foreground disabled:opacity-60"
                    >
                      {guardandoEdicion ? <Spinner size="sm" /> : <Check className="h-3.5 w-3.5" />}
                      Guardar
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={entrarEdicion}
                    className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-[6px] border border-brinks-border bg-card px-2 text-[11px] font-medium text-brinks-foreground hover:bg-brinks-surface-hover"
                  >
                    <Settings2 className="h-3.5 w-3.5" /> Editar
                  </button>
                )
              ) : null}
            </div>

            {/* B-116 — the confirmation: what changes, that the closing is recalculated, and the
                observation, required. */}
            <Dialog
              open={confirmandoCorreccion}
              onOpenChange={(abre) => (!abre && !guardandoEdicion ? setConfirmandoCorreccion(false) : null)}
            >
              {resultadoCorreccion ? (
                // Saved: the question is over, so only the result — no changes, no observation box.
                <DialogContent className="brinks-scope font-redhat max-w-md">
                  <DialogHeader className="text-left">
                    <div className="flex min-w-0 items-center gap-1">
                      <DialogTitle className="flex items-center gap-2 text-[16px] font-semibold text-brinks-foreground">
                        <CheckCircle className="h-4 w-4 text-brinks-success" />
                        Corrección guardada
                      </DialogTitle>
                      <BotonGuia guiaId="dialogo-correccion-guardada" titulo="Corrección guardada" texto="La corrección quedó guardada y el cierre se recalculó. Aquí ves lo que todavía queda pendiente." />
                    </div>
                    <DialogDescription className="text-[12.5px] leading-relaxed text-brinks-muted-foreground">
                      {resultadoCorreccion.texto}
                    </DialogDescription>
                  </DialogHeader>
                  {resultadoCorreccion.pendientes.length ? (
                    <ul role="status" className="list-disc space-y-1 rounded-[8px] border border-brinks-border bg-brinks-surface py-2 pl-7 pr-3 text-[12.5px] text-brinks-foreground">
                      {resultadoCorreccion.pendientes.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  ) : null}
                  <DialogFooter>
                    <Button size="sm" onClick={() => setConfirmandoCorreccion(false)}>Entendido</Button>
                  </DialogFooter>
                </DialogContent>
              ) : (
              <DialogContent className="brinks-scope font-redhat max-w-md">
                <DialogHeader className="text-left">
                  <div className="flex min-w-0 items-center gap-1">
                    <DialogTitle className="flex items-center gap-2 text-[16px] font-semibold text-brinks-foreground">
                      <AlertTriangle className="h-4 w-4 text-brinks-warning" />
                      ¿Guardar la corrección?
                    </DialogTitle>
                    <BotonGuia guiaId="dialogo-guardar-correccion" titulo="Guardar la corrección" texto="Revisa qué cifras cambian, escribe por qué las corriges y guarda. El cierre se recalcula al momento y la corrección queda con tu nombre; no se puede deshacer." />
                  </div>
                  <DialogDescription className="text-[12.5px] leading-relaxed text-brinks-muted-foreground">
                    El cierre se recalcula al momento con estos valores y la corrección queda registrada con tu
                    nombre. No se puede deshacer; solo se puede volver a corregir.
                  </DialogDescription>
                </DialogHeader>
                <ul {...guia("Lo que cambia", "Cada cifra con su valor anterior y el nuevo.")} className="space-y-1.5 rounded-[8px] border border-brinks-warning-border bg-brinks-warning-soft px-3 py-2">
                  {/* Taken when "Guardar" was pressed: what is about to change. */}
                  {cambiosConfirmados.map(({ field: f, antes, despues }) => (
                    <li key={f.fieldName} className="text-[12.5px] text-brinks-foreground">
                      <span className="font-medium">{nombreDeCampo(f)}</span>
                      <span className="block tabular-nums text-brinks-muted-foreground">
                        {datoLegible(antes, f.format)} →{" "}
                        <span className="font-semibold text-brinks-foreground">
                          {/* The new figure written like the old one; a value that is not a number, as typed. */}
                          {montoEscrito(despues) ? datoLegible(montoEscrito(despues), f.format) : despues}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-1">
                  <label htmlFor="observacion-correccion" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
                    Observación (obligatoria)
                  </label>
                  <Textarea
                    {...guia("Observación", "Obligatoria: por qué corriges el cierre. Queda registrada con tu nombre.")}
                    id="observacion-correccion"
                    rows={3}
                    maxLength={1000}
                    value={razonEdicion}
                    disabled={guardandoEdicion}
                    placeholder="Por qué se corrige este cierre — queda registrado con tu nombre"
                    onChange={(e) => setRazonEdicion(e.target.value)}
                    className="bg-card text-brinks-foreground"
                  />
                </div>
                {errorEdicion ? <p role="alert" className="text-[12px] text-brinks-danger">{errorEdicion}</p> : null}
                <DialogFooter className="gap-2">
                  <Button variant="outline" size="sm" disabled={guardandoEdicion} onClick={() => setConfirmandoCorreccion(false)}>
                    Cancelar
                  </Button>
                  <Button size="sm" className="gap-1.5" disabled={guardandoEdicion || !razonEdicion.trim()} onClick={guardarEdicion}>
                    {guardandoEdicion ? <Spinner size="sm" /> : <Check className="h-3.5 w-3.5" />}
                    Guardar y recalcular
                  </Button>
                </DialogFooter>
              </DialogContent>
              )}
            </Dialog>

            <div className={cn("max-h-[58vh] overflow-y-auto p-2.5", SCROLL_OCULTO)}>
              {estadoGlory ? (
                <div
                  className={cn(
                    "mb-1 rounded-[8px] px-2.5 py-2",
                    editandoDatos && estadoGlory.editable ? "border border-brinks-border-strong bg-card" : "bg-brinks-surface"
                  )}
                >
                  <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-brinks-muted-foreground">
                    Configuración de la oficina
                  </h4>
                  <div className="flex items-start justify-between gap-3">
                    <span className="shrink-0 pt-0.5 text-[12.5px] text-brinks-foreground">Máquina GLORY</span>
                    <GloryCelda
                      estado={estadoGlory}
                      soloLectura={!editandoDatos}
                      onCambiar={glory.cambiar}
                      onHistorial={glory.verHistorial}
                      className="flex min-w-0 flex-col items-end text-right"
                    />
                  </div>
                  {avisoGlory ? (
                    <div role="status" className="mt-2 rounded-[6px] border border-brinks-success-border bg-brinks-success-soft px-2 py-1.5 text-[11.5px] text-brinks-success">
                      <p className="text-[11.5px] leading-snug">{avisoGlory}</p>
                      {puedeRehacerAqui ? (
                        <button
                          type="button"
                          onClick={() => setRehacerAbierto(true)}
                          className="mt-1 inline-flex items-center gap-1 font-medium underline underline-offset-2"
                        >
                          <RotateCcw className="h-3 w-3" /> Rehacer este cierre para aplicarlo
                        </button>
                      ) : (
                        <p className="mt-0.5 text-[11.5px] leading-snug text-brinks-muted-foreground">
                          {rehacerEnElDia
                            ? "Para aplicarlo a este día, usa «Rehacer el día» en el panel del día."
                            : "Este cierre conserva su lectura hasta que se vuelva a evaluar."}
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {panelRow && grupos.length > 0 ? (
                grupos.map(([grupo, campos], i) => (
                  <div key={grupo} className={cn("py-2", i > 0 && "border-t border-brinks-border")}>
                    <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-brinks-muted-foreground">
                      {grupo}
                    </h4>
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {campos.map((field) => {
                        const editable = editandoDatos && puedeCorregir && field.correctable;
                        const valor = valorActual(field.fieldName);
                        const error = erroresCampo[field.fieldName];
                        if (!editable) {
                          return (
                            <VariableDesplegable
                              key={field.fieldName}
                              nombre={nombreDeCampo(field)}
                              valor={datoLegible(valor, field.format)}
                              numerico
                              detalles={detallesDeCampo(field)}
                            />
                          );
                        }
                        return (
                          <div
                            key={field.fieldName}
                            className={cn(
                              "rounded-[8px] px-2 py-1.5",
                              editable ? "border border-brinks-border-strong bg-card" : "bg-brinks-surface"
                            )}
                          >
                            <span className="block text-[10px] leading-tight text-brinks-muted-foreground">
                              {nombreDeCampo(field)}
                            </span>
                            {editable ? (
                              <>
                                <input
                                  value={valoresEdicion[field.fieldName] ?? ""}
                                  onChange={(e) =>
                                    setValoresEdicion((prev) => ({ ...prev, [field.fieldName]: e.target.value }))
                                  }
                                  aria-label={nombreDeCampo(field)}
                                  className="mt-0.5 h-6 w-full rounded-[6px] border border-brinks-border bg-card px-1.5 text-[13px] font-semibold tabular-nums outline-none transition focus:border-brinks-border-strong"
                                />
                                {error ? <p className="mt-0.5 truncate text-[9.5px] text-brinks-danger" title={error}>{error}</p> : null}
                              </>
                            ) : (
                              <span className="mt-0.5 block truncate text-[13px] font-semibold leading-tight tabular-nums text-brinks-foreground">
                                {valor === null || valor === undefined || valor === "" ? "—" : String(valor)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <p className="p-2 text-[12.5px] text-brinks-muted-foreground">
                  {panelRow
                    ? "Este cliente no tiene campos de panel configurados."
                    : "No se pudo cargar la fila del panel para este cierre, así que no hay valores capturados que editar aquí."}
                </p>
              )}

              <div className={cn("py-2", grupos.length > 0 && "border-t border-brinks-border")}>
                <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-brinks-muted-foreground">
                  Resultados calculados
                </h4>
                {resultados.length === 0 ? (
                  <p className="p-1 text-[12.5px] text-brinks-muted-foreground">
                    Todavía no hay resultados para este cierre.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {resultados.map((r) => (
                      <VariableDesplegable
                        key={r.key}
                        nombre={nombreDeCalculo(nombres, r.key)}
                        valor={r.resolved ? montoLegible(r.value) : "Sin resolver"}
                        tono={r.resolved ? "normal" : "apagado"}
                        numerico={r.resolved}
                        detalles={detallesDeCalculo({ nombres, resultado: r, entradas: entradasDeCalculo.get(r.key) })}
                      />
                    ))}
                  </div>
                )}
                <p className="mt-1.5 px-1 text-[10px] text-brinks-muted-foreground">
                  Calculado automáticamente a partir de los campos capturados — no editable.
                </p>
              </div>
            </div>
          </section>

          {/* Documento */}
          <section {...guia("Documento", "El documento que estás viendo. Elige otro en la lista de arriba o descárgalo con su ícono.")} className="flex flex-col overflow-hidden rounded-[12px] border border-brinks-border-strong bg-brinks-surface shadow-brinks-card">
            <div className="flex min-h-11 items-center justify-between gap-2 border-b bg-card px-3.5 py-2">
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[7px] bg-brinks-surface text-brinks-muted-foreground">
                  <FileText className="h-4 w-4" />
                </span>
                {documentos.length + adjuntos.length > 0 ? (
                  <Select
                    value={docSeleccionado?.id || ""}
                    onValueChange={(id) => setDocSeleccionado([...documentos, ...adjuntos].find((d) => d.id === id) || null)}
                  >
                    {/* The trigger is a fixed-height row (`h-7`), so the raw filename rides in the
                        tooltip here instead of on a second line — the same compromise
                        CierreManualDialog already makes for a key that does not fit. */}
                    <SelectTrigger
                      className="h-7 min-w-0 max-w-[220px] rounded-[6px] border-0 bg-transparent px-1 text-[12.5px] font-medium shadow-none"
                      title={archivoDeDocumento(docSeleccionado) || docSeleccionado?.sourceKey || ""}
                    >
                      <SelectValue>
                        {docSeleccionado?.attachment
                          ? ADJUNTO_DEL_CORREO
                          : docSeleccionado?.sourceKey ? nombreDeFuente(nombres, docSeleccionado.sourceKey) : "Documento"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="brinks-scope font-redhat">
                      {[...documentos, ...adjuntos].map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          <span className="block">{d.attachment ? ADJUNTO_DEL_CORREO : nombreDeFuente(nombres, d.sourceKey)}</span>
                          <span className="block text-[10.5px] text-brinks-muted-foreground">
                            {archivoDeDocumento(d) || d.sourceKey}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-[12.5px] text-brinks-muted-foreground">Sin documentos</span>
                )}
              </span>
              {docSeleccionado?.url ? (
                <a
                  href={docSeleccionado.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex shrink-0 items-center gap-1 text-brinks-muted-foreground hover:text-brinks-foreground"
                  aria-label="Descargar documento"
                >
                  <Download className="h-3.5 w-3.5" />
                </a>
              ) : null}
            </div>
            <div className={cn("grid min-h-[360px] max-h-[58vh] flex-1 place-items-center overflow-auto bg-muted p-2", SCROLL_OCULTO)}>
              {docSeleccionado?.url && docSeleccionado.attachment && !adjuntoVisible(docSeleccionado) ? (
                <p className="p-6 text-center text-[12.5px] text-brinks-muted-foreground">
                  Este adjunto no se puede ver aquí; descárgalo con el botón de arriba.
                </p>
              ) : docSeleccionado?.url && docSeleccionado.attachment && viewerKind(docSeleccionado) === "spreadsheet" ? (
                // An attachment is no document of the closing: there are no office rows to preview.
                <iframe
                  key={docSeleccionado.url}
                  src={spreadsheetViewerUrl(docSeleccionado.url)}
                  title={docSeleccionado.filename}
                  className="h-[54vh] w-full rounded-[6px] border bg-card"
                />
              ) : docSeleccionado?.url ? (
                viewerKind(docSeleccionado) === "spreadsheet" || viewerKind(docSeleccionado) === "spreadsheet-local" ? (
                  // Opens on the rows about this closing (its day, its office); "Ver completo"
                  // is the viewer this branch always drew, built below exactly as before.
                  <VistaPreviaHoja
                    key={docSeleccionado.id}
                    token={token}
                    mantisBusinessId={mantisBusinessId}
                    closingId={closingId}
                    documento={docSeleccionado}
                    vistaCompleta={
                      viewerKind(docSeleccionado) === "spreadsheet-local" ? (
                        <VistaHojaDeCalculo
                          key={docSeleccionado.url}
                          url={docSeleccionado.url}
                          nombre={archivoDeDocumento(docSeleccionado)}
                        />
                      ) : (
                        <iframe
                          key={docSeleccionado.url}
                          src={spreadsheetViewerUrl(docSeleccionado.url)}
                          title={docSeleccionado.filename}
                          className="h-[54vh] w-full rounded-[6px] border bg-card"
                        />
                      )
                    }
                  />
                ) : viewerKind(docSeleccionado) === "image" ? (
                  <VisorImagen url={docSeleccionado.url} nombre={docSeleccionado.filename} />
                ) : (
                  <iframe
                    key={docSeleccionado.url}
                    src={docSeleccionado.url}
                    title={docSeleccionado.filename}
                    className="h-[54vh] w-full rounded-[6px] border bg-card"
                  />
                )
              ) : (
                <p className="p-6 text-center text-[12.5px] text-brinks-muted-foreground">
                  {docSeleccionado
                    ? unavailableMessage(docSeleccionado)
                    : errorDocumentos || "No hay documento cargado para esta oficina."}
                </p>
              )}
            </div>
          </section>

          {/* Alertas */}
          {alertasVisibles ? (
            <section {...guia("Alertas de cuadre", "Las diferencias que encontró el cuadre, de la más grave a la más leve. \"Marcar atendida\" la cierra con tu observación; debajo están las que ya se atendieron.")} className="overflow-hidden rounded-[12px] border border-brinks-border-strong bg-card shadow-brinks-card">
              <div className="grid min-h-11 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b bg-brinks-surface px-3.5 py-2">
                <div className="min-w-0">
                  <h3 className="truncate text-[12px] font-semibold text-brinks-foreground">Alertas de cuadre</h3>
                  <p className="mt-0.5 truncate text-[10.5px] text-brinks-muted-foreground">Validaciones y diferencias</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAlertasVisibles(false)}
                  className="inline-flex h-7 shrink-0 items-center gap-1 rounded-[6px] px-2 text-[11px] font-medium text-brinks-muted-foreground hover:bg-card"
                >
                  Ocultar
                  <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
                </button>
              </div>
              <div className={cn("max-h-[58vh] space-y-2 overflow-y-auto p-3", SCROLL_OCULTO)}>
                {alertasAbiertas.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-[9px] border border-brinks-success-border bg-brinks-success-soft px-3 py-2.5 text-[12.5px] text-brinks-success">
                    <CheckCircle className="h-4 w-4 shrink-0" />
                    <span className="font-medium">Sin alertas abiertas</span>
                  </div>
                ) : (
                  alertasAbiertas.map((a) => (
                    <AlertaDelCierre
                      key={a.id}
                      alerta={a}
                      nombres={nombres}
                      closingId={closingId}
                      token={token}
                      mantisBusinessId={mantisBusinessId}
                      onAtendida={alertaAtendida}
                      documentos={documentos}
                      docSeleccionado={docSeleccionado}
                      onVerDocumento={setDocSeleccionado}
                      onIrACierre={onIrACierre ? (destino, fechaDestino) => onIrACierre({
                        destino,
                        fechaDestino,
                        fechaDesde: detalle.closing?.businessDate,
                        oficina: detalle.closing?.officeCode,
                        cliente: detalle.closing?.clientCode,
                      }) : undefined}
                      puedeAtender={acciones.atender}
                    />
                  ))
                )}
                {/* B-56 — said once, after the first alert attended here. Attending records a
                    judgement; it does not make the closing agree, and the rule raises a fresh alert
                    on the next evaluation while the difference holds. */}
                {avisoReevaluacion ? (
                  <p role="status" className="rounded-[9px] border border-brinks-warning-border bg-brinks-warning-soft px-2.5 py-2 text-[11.5px] leading-snug text-brinks-warning">
                    {AVISO_ALERTAS}
                  </p>
                ) : null}
                {resultadoAjuste ? (
                  <p role="status" className="rounded-[9px] border border-brinks-border bg-brinks-surface px-2.5 py-2 text-[11.5px] leading-snug text-brinks-foreground">
                    {resultadoAjuste}
                  </p>
                ) : null}
                {alertasAtendidas.length > 0 ? (
                  <>
                    <p className="border-t pt-2 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
                      Ya atendidas
                    </p>
                    {alertasAtendidas.map((a) => (
                      <AlertaDelCierre
                        key={a.id}
                        alerta={a}
                        nombres={nombres}
                        closingId={closingId}
                        token={token}
                        mantisBusinessId={mantisBusinessId}
                        onEditada={observacionEditada}
                        puedeAtender={acciones.atender}
                        documentos={documentos}
                        docSeleccionado={docSeleccionado}
                        onVerDocumento={setDocSeleccionado}
                      />
                    ))}
                  </>
                ) : null}
              </div>
            </section>
          ) : (
            <button
              type="button"
              onClick={() => setAlertasVisibles(true)}
              className="absolute right-2 top-2 z-10 inline-flex h-7 items-center gap-1.5 rounded-[8px] border border-brinks-border-strong bg-card px-2.5 text-[11px] font-medium text-brinks-foreground shadow-brinks-card hover:bg-brinks-surface-hover"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Mostrar alertas
            </button>
          )}
        </div>

        {/* Fuentes + Pendientes para cuadrar */}
        <div className="grid gap-2 xl:grid-cols-[minmax(0,1.65fr)_minmax(280px,0.7fr)]">
          <section {...guia("Fuentes de datos", "Cada documento que alimenta el cierre y si ya llegó. Toca uno para verlo.")} className="overflow-hidden rounded-[12px] border border-brinks-border-strong bg-card shadow-brinks-card">
            <div className="border-b bg-brinks-surface px-3.5 py-2.5">
              <h3 className="text-[12px] font-semibold text-brinks-foreground">Fuentes de datos del cierre</h3>
              <p className="mt-0.5 text-[10.5px] text-brinks-muted-foreground">
                Documentos y fuentes usados para el cuadre — clic para verlos
              </p>
            </div>
            <ul className="grid gap-2 p-3 sm:grid-cols-2 xl:grid-cols-3">
              {pasos.map((p) => {
                const documento = documentos.find((d) => d.sourceKey === p.label);
                const tieneDocumento = p.ok && Boolean(documento);
                return (
                  <li key={p.key}>
                    <button
                      type="button"
                      onClick={() => seleccionarDocumentoDeFuente(p)}
                      disabled={!tieneDocumento}
                      className={cn(
                        "flex w-full min-w-0 items-start justify-between gap-2 rounded-[8px] border px-2.5 py-2 text-left transition-colors",
                        tieneDocumento
                          ? "cursor-pointer hover:border-brinks-border-strong hover:bg-brinks-surface"
                          : "cursor-not-allowed opacity-80",
                        docSeleccionado?.sourceKey === p.label && tieneDocumento && "border-brinks-border-strong bg-brinks-surface"
                      )}
                    >
                      <span className="flex min-w-0 items-start gap-2">
                        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-brinks-muted-foreground" />
                        <span className="min-w-0">
                          <span className="block truncate text-[12.5px]">{nombreDeFuente(nombres, p.label)}</span>
                          <span className="block truncate text-[10.5px] text-brinks-muted-foreground">
                            {archivoDeDocumento(documento) || p.label}
                          </span>
                        </span>
                      </span>
                      <span className={cn("shrink-0 text-[11.5px] font-medium", p.ok ? "text-brinks-success" : "text-brinks-warning")}>
                        {p.ok ? "Cargado" : "Pendiente"}
                      </span>
                    </button>
                  </li>
                );
              })}
              {adjuntos.map((adjunto) => (
                <li key={adjunto.id}>
                  <button
                    type="button"
                    onClick={() => setDocSeleccionado(adjunto)}
                    className={cn(
                      "flex w-full min-w-0 cursor-pointer items-start justify-between gap-2 rounded-[8px] border px-2.5 py-2 text-left transition-colors hover:border-brinks-border-strong hover:bg-brinks-surface",
                      docSeleccionado?.id === adjunto.id && "border-brinks-border-strong bg-brinks-surface"
                    )}
                  >
                    <span className="flex min-w-0 items-start gap-2">
                      <Paperclip className="mt-0.5 h-4 w-4 shrink-0 text-brinks-muted-foreground" />
                      <span className="min-w-0">
                        <span className="block truncate text-[12.5px]">{ADJUNTO_DEL_CORREO}</span>
                        <span className="block truncate text-[10.5px] text-brinks-muted-foreground" title={adjunto.filename}>
                          {adjunto.filename}
                        </span>
                      </span>
                    </span>
                    <span className="shrink-0 text-[11.5px] font-medium text-brinks-muted-foreground">Adjunto</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section {...guia("Pendientes para cuadrar", "Lo que todavía falta para que el cierre se pueda cuadrar: los documentos que la oficina no ha enviado y los datos que no se han podido leer.")} className="overflow-hidden rounded-[12px] border border-brinks-border-strong bg-card shadow-brinks-card">
            <div className="border-b bg-brinks-surface px-3.5 py-2.5">
              <h3 className="text-[12px] font-semibold text-brinks-foreground">Pendientes para cuadrar</h3>
              <p className="mt-0.5 text-[10.5px] text-brinks-muted-foreground">
                Documentos y datos que faltan para resolver el cierre
              </p>
            </div>
            <div className="p-3">
              {pendientes.length || documentosSinEnviar.length ? (
                <ul className="divide-y divide-brinks-border rounded-[9px] border border-brinks-border px-2.5">
                  {documentosSinEnviar.map((documento) => (
                    <li key={`documento-${documento}`} className="flex items-start gap-2 py-2 first:pt-1 last:pb-1">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brinks-warning" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12.5px]">{documento}</span>
                        <span className="block text-[10.5px] leading-snug text-brinks-muted-foreground">
                          La oficina no lo ha enviado
                        </span>
                      </span>
                      <span className="shrink-0 text-[11.5px] font-medium text-brinks-warning">Pendiente</span>
                    </li>
                  ))}
                  {pendientes.map((p) => (
                    <li key={p.fuente} className="flex items-start gap-2 py-2 first:pt-1 last:pb-1">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brinks-warning" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12.5px]">{nombreDeFuente(nombres, p.fuente)}</span>
                        <span className="block text-[10.5px] leading-snug text-brinks-muted-foreground">
                          {p.cargado
                            ? "Cargado, pero no trae: "
                            : "No ha llegado. Falta: "}
                          {p.campos.map((c) => nombreDeDato(nombres, `${p.fuente}.${c.clave}`)).join(", ")}
                        </span>
                      </span>
                      <span
                        className={cn(
                          "shrink-0 text-[11.5px] font-medium",
                          p.cargado ? "text-brinks-muted-foreground" : "text-brinks-warning"
                        )}
                      >
                        {p.cargado ? "Incompleto" : "Pendiente"}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex items-center gap-2 rounded-[9px] border border-brinks-success-border bg-brinks-success-soft px-3 py-2.5 text-[12.5px] text-brinks-success">
                  <CheckCircle className="h-4 w-4 shrink-0" />
                  <span className="font-medium">No falta ningún dato</span>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* B-101 — the office's pending notes; nothing for a client with no day reconciliation. */}
        <PendientesOficina
          token={token}
          mantisBusinessId={mantisBusinessId}
          clientCode={objetivo.cliente}
          officeCode={objetivo.oficina}
          puedeCambiar={acciones.atender}
        />

        {/* B-154 — the reversals recorded on this closing (form CN-FT-045); nothing for a client without them. */}
        <ReversasOficina
          token={token}
          mantisBusinessId={mantisBusinessId}
          closingId={closingId}
          fechaCierre={detalle.closing?.businessDate}
          puedeCambiar={acciones.atender}
        />

        {llegadas.length > 0 ? (
          <section {...guia("Cómo llegaron los documentos", "Cada documento que llegó al cierre: por correo, por \"Procesar el día\" o subido aquí, quién lo envió, cuándo y qué versión se está leyendo.")} className="overflow-hidden rounded-[12px] border border-brinks-border-strong bg-card shadow-brinks-card">
            <div className="border-b bg-brinks-surface px-3.5 py-2.5">
              <h3 className="text-[12px] font-semibold text-brinks-foreground">Cómo llegaron los documentos</h3>
              <p className="mt-0.5 text-[10.5px] text-brinks-muted-foreground">
                Cada documento con su origen, quién lo envió o lo cargó y cuándo; el que se está leyendo, marcado como actual
              </p>
            </div>
            <ul className="divide-y divide-brinks-border px-3.5">
              {[...llegadas].reverse().map((llegada) => {
                const palabras = llegadaEnPalabras(llegada, fechaHoraBogota);
                return (
                  <li key={llegada.seq} className="flex items-start justify-between gap-3 py-2">
                    <span className="flex min-w-0 items-start gap-2">
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-brinks-muted-foreground" />
                      <span className="min-w-0">
                        <span className="block truncate text-[12.5px]">
                          {palabras.evidencia ? "Adjunto guardado como evidencia" : nombreDeFuente(nombres, llegada.documentKey)}
                        </span>
                        <span className="block truncate text-[10.5px] text-brinks-muted-foreground" title={palabras.asunto || undefined}>
                          {[palabras.archivo, palabras.dentroDe].filter(Boolean).join(" · ")}
                        </span>
                        {palabras.motivo ? (
                          <span className="block text-[10.5px] text-brinks-muted-foreground">{palabras.motivo}</span>
                        ) : null}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-[11.5px] font-medium">
                        {palabras.via}
                        {palabras.version ? ` · ${palabras.version}` : ""}
                        {palabras.actual ? <span className="ml-1 text-brinks-success">· actual</span> : null}
                      </span>
                      <span className="block text-[10.5px] text-brinks-muted-foreground">
                        {[palabras.quien, palabras.cuando].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>
    </>
  );
}

// A carried alert's "Ir al cierre del …" opens the closing the difference started on, in this same
// dialog, with a way back. The visited closings are a stack here, so the five screens that open the
// dialog change nothing. Its panel row is asked for (`filterOffices`, that date and office), as the
// Alertas feed does on "Ver cierre", so its extracted values and actions are those of the panel.
export function CierreDetailDialog({ closingId, panelRow, token, mantisBusinessId, onOpenChange, onTrabajarAMano, onCambio }) {
  const [visitados, setVisitados] = useState([]);
  useEffect(() => setVisitados([]), [closingId]);

  const actual = visitados.length ? visitados[visitados.length - 1] : null;
  const irACierre = ({ destino, fechaDestino, fechaDesde, oficina, cliente }) => {
    if (!destino) return;
    setVisitados((pila) => [...pila, { closingId: destino, fechaDesde, fila: null }]);
    if (!fechaDestino) return;
    const filtros = { startDate: fechaDestino, endDate: fechaDestino, office: oficina || undefined };
    filterOffices(token, filtros, 100, mantisBusinessId, "brinks", cliente)
      .then((r) => {
        const fila = (r?.success ? r.data || [] : []).find((row) => row.closing_id === destino) || null;
        if (!fila) return;
        setVisitados((pila) => pila.map((v) => (v.closingId === destino ? { ...v, fila } : v)));
      })
      .catch(() => {});
  };
  const volver = () => setVisitados((pila) => pila.slice(0, -1));

  return (
    <Dialog open={!!closingId} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "brinks-scope font-redhat flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:h-auto sm:max-h-[95vh] sm:w-[calc(100vw-24px)] sm:max-w-[1480px] sm:rounded-[18px] sm:border"
        )}
      >
        {actual ? (
          <div className="flex shrink-0 items-center border-b bg-brinks-surface px-3.5 py-1.5">
            <button
              type="button"
              onClick={volver}
              className="inline-flex h-7 items-center gap-1.5 rounded-[6px] px-2 text-[12px] font-medium text-brinks-foreground hover:bg-card"
            >
              <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
              Volver al cierre del {fechaDMY(actual.fechaDesde)}
            </button>
          </div>
        ) : null}
        {closingId ? (
          <Contenido
            key={actual ? actual.closingId : closingId}
            closingId={actual ? actual.closingId : closingId}
            panelRow={actual ? actual.fila : panelRow}
            token={token}
            mantisBusinessId={mantisBusinessId}
            onTrabajarAMano={onTrabajarAMano}
            onCambio={onCambio}
            onIrACierre={irACierre}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default CierreDetailDialog;
